import BaseApiClient from "../api/baseApiClient.service";
import type {
	GruppeDTO,
	OrgaAdminGruppeWithAssignmentStatistics,
	OrgaAdminGruppeDTO,
	StudentKlausurDetailedDTO,
} from "./orgaAdmin.types";

/**
 * Response type for the number of korrektur steps assigned to a user in a gruppe
 */
export type KorrekturStepsCountForUsersInGruppeResponse = {
	[userId: string]: number;
};

/**
 * Service for managing Gruppe operations for organization admins
 */
export class OrgaAdminGruppeService {
	private static instance: OrgaAdminGruppeService;

	private baseApi: BaseApiClient;

	// Base path mounted on the backend for gruppe routes
	private baseApiSubPath = "/orga-admin/gruppe";

	private constructor() {
		this.baseApi = BaseApiClient.getInstance();
	}

	public static getInstance(): OrgaAdminGruppeService {
		if (!OrgaAdminGruppeService.instance) {
			OrgaAdminGruppeService.instance = new OrgaAdminGruppeService();
			OrgaAdminGruppeService.instance.baseApi = BaseApiClient.getInstance();
		}
		return OrgaAdminGruppeService.instance;
	}

	/**
	 * Create a new gruppe in an organization
	 * @param organizationId The organization ID
	 * @param title The title of the gruppe
	 * @returns Created gruppe (shape determined by backend)
	 */
	public async createGruppe(
		organizationId: string,
		title: string,
	): Promise<GruppeDTO> {
		return this.baseApi.post<GruppeDTO, { title: string }>(
			`${this.baseApiSubPath}/${organizationId}/gruppe`,
			{ title },
		);
	}

	/**
	 * Get details of an gruppe
	 * @param organizationId The organization ID
	 * @param gruppeId The gruppe ID
	 */
	public async getGruppe(
		organizationId: string,
		gruppeId: string,
	): Promise<GruppeDTO> {
		return this.baseApi.get<GruppeDTO>(
			`${this.baseApiSubPath}/${organizationId}/gruppe/${gruppeId}`,
		);
	}

	/**
	 * Get all gruppe in an organization (with optional statistics)
	 * @param organizationId The organization ID
	 */
	public async getGruppenInOrganization(
		organizationId: string,
		statistics = false,
	): Promise<OrgaAdminGruppeDTO[] | OrgaAdminGruppeWithAssignmentStatistics[]> {
		return this.baseApi.get<
			OrgaAdminGruppeDTO[] | OrgaAdminGruppeWithAssignmentStatistics[]
		>(
			`${this.baseApiSubPath}/${organizationId}/gruppen?statistics=${statistics.toString()}`,
		);
	}

	/**
	 * Get a gruppe with assignment statistics
	 * @param organizationId The organization ID
	 * @param gruppeId The gruppe ID
	 */
	public async getGruppeWithAssignmentStatistics(
		organizationId: string,
		gruppeId: string,
	): Promise<OrgaAdminGruppeWithAssignmentStatistics> {
		return this.baseApi.get<OrgaAdminGruppeWithAssignmentStatistics>(
			`${this.baseApiSubPath}/${organizationId}/gruppe/${gruppeId}/statistics`,
		);
	}

	/**
	 * Get ONLY the statistics for a gruppe (can be used to refresh the statistics)
	 * @param organizationId The organization ID
	 * @param gruppeId The gruppe ID
	 */
	public async getStatisticsForGruppe(
		organizationId: string,
		gruppeId: string,
	): Promise<OrgaAdminGruppeWithAssignmentStatistics> {
		return this.baseApi.get<OrgaAdminGruppeWithAssignmentStatistics>(
			`${this.baseApiSubPath}/${organizationId}/gruppe/${gruppeId}/statistics`,
		);
	}

	/**
	 * Assign a user to an gruppe
	 * @param organizationId The organization ID
	 * @param gruppeId The gruppe ID
	 * @param userId The user ID to assign
	 */
	public async assignUserToGruppe(
		organizationId: string,
		gruppeId: string,
		userId: string,
	): Promise<void> {
		await this.baseApi.put(
			`${this.baseApiSubPath}/${organizationId}/gruppe/${gruppeId}/users/${userId}`,
			{},
		);
	}

	/**
	 * Unassign a user from an gruppe
	 * @param organizationId The organization ID
	 * @param gruppeId The gruppe ID
	 * @param userId The user ID to unassign
	 */
	public async unassignUserFromGruppe(
		organizationId: string,
		gruppeId: string,
		userId: string,
	): Promise<void> {
		await this.baseApi.delete(
			`${this.baseApiSubPath}/${organizationId}/gruppe/${gruppeId}/users/${userId}`,
		);
	}

	/**
	 * Add users to an gruppe
	 * @param organizationId The organization ID
	 * @param gruppeId The gruppe ID
	 * @param userIds The user IDs to add
	 */
	public async addUsersToGruppe(
		organizationId: string,
		gruppeId: string,
		userIds: string[],
	): Promise<void> {
		await this.baseApi.put(
			`${this.baseApiSubPath}/${organizationId}/gruppe/${gruppeId}/users/bulk`,
			{ userIds },
		);
	}

	/**
	 * Remove users from an gruppe
	 * @param organizationId The organization ID
	 * @param gruppeId The gruppe ID
	 * @param userIds The user IDs to remove
	 */
	public async removeUsersFromGruppe(
		organizationId: string,
		gruppeId: string,
		userIds: string[],
	): Promise<void> {
		await this.baseApi.delete(
			`${this.baseApiSubPath}/${organizationId}/gruppe/${gruppeId}/users/bulk`,
			{ userIds },
		);
	}

	/**
	 * Get the number of korrektur steps assigned to a user in a gruppe
	 * @param organizationId The organization ID
	 * @param gruppeId The gruppe ID
	 * @param userId The user ID
	 */
	public async getKorrekturStepsCountForUser(
		organizationId: string,
		gruppeId: string,
		userId: string,
	): Promise<number> {
		return this.baseApi.get<number>(
			`${this.baseApiSubPath}/${organizationId}/gruppe/${gruppeId}/users/${userId}/korrektur-steps`,
		);
	}

	/**
	 * Get the number of korrektur steps assigned to a user in a gruppe
	 * @param organizationId The organization ID
	 * @param gruppeId The gruppe ID
	 * @param userIds The user IDs
	 */
	public async getKorrekturStepsCountForUsersInGruppe(
		organizationId: string,
		gruppeId: string,
		userIds: string[],
	): Promise<KorrekturStepsCountForUsersInGruppeResponse> {
		return this.baseApi.post<
			KorrekturStepsCountForUsersInGruppeResponse,
			{ userIds: string[] }
		>(
			`${this.baseApiSubPath}/${organizationId}/gruppe/${gruppeId}/users/korrektur-steps/bulk`,
			{ userIds },
		);
	}

	/**
	 * Assign a student klausur to an gruppe
	 * @param organizationId The organization ID
	 * @param gruppeId The gruppe ID
	 * @param studentKlausurId The student klausur ID to assign
	 */
	public async assignStudentKlausurToGruppe(
		organizationId: string,
		gruppeId: string,
		studentKlausurId: string,
	): Promise<void> {
		await this.baseApi.put(
			`${this.baseApiSubPath}/${organizationId}/gruppe/${gruppeId}/student-klausuren/${studentKlausurId}`,
			{},
		);
	}

	/**
	 * Assign a klausur to an gruppe
	 * @param organizationId The organization ID
	 * @param gruppeId The gruppe ID
	 * @param klausurId The klausur ID to assign
	 */
	public async assignKlausurToGruppe(
		organizationId: string,
		gruppeId: string,
		klausurId: string,
	): Promise<void> {
		await this.baseApi.put(
			`${this.baseApiSubPath}/${organizationId}/gruppe/${gruppeId}/klausur/${klausurId}`,
			{},
		);
	}

	/**
	 * Get detailed student klausuren in a gruppe
	 * @param organizationId The organization ID
	 * @param gruppeId The gruppe ID
	 */
	public async getStudentKlausurenInGruppe(
		organizationId: string,
		gruppeId: string,
	): Promise<StudentKlausurDetailedDTO[]> {
		return this.baseApi.get<StudentKlausurDetailedDTO[]>(
			`${this.baseApiSubPath}/${organizationId}/gruppe/${gruppeId}/student-klausuren`,
		);
	}

	/**
	 * Assign a korrektur step to a korrektor in a gruppe
	 * @param organizationId The organization ID
	 * @param gruppeId The gruppe ID
	 * @param korrekturStepId The korrektur step ID
	 * @param korrektorId The korrektor ID to assign
	 */
	public async assignKorrekturStepToKorrektor(
		organizationId: string,
		gruppeId: string,
		korrekturStepId: string,
		korrektorId: string,
	): Promise<void> {
		await this.baseApi.put(
			`${this.baseApiSubPath}/${organizationId}/gruppe/${gruppeId}/korrektur-steps/${korrekturStepId}/korrektor/${korrektorId}`,
			{},
		);
	}

	/**
	 * Remove a korrektur step from a korrektor in a gruppe
	 * @param organizationId The organization ID
	 * @param gruppeId The gruppe ID
	 * @param korrekturStepId The korrektur step ID
	 */
	public async removeKorrekturStepFromKorrektor(
		organizationId: string,
		gruppeId: string,
		korrekturStepId: string,
	): Promise<void> {
		return await OrgaAdminGruppeService.getInstance().assignKorrekturStepToKorrektor(
			organizationId,
			gruppeId,
			korrekturStepId,
			"null",
		);
	}

	/**
	 * Assign multiple korrektur steps to a korrektor in a gruppe
	 * @param organizationId The organization ID
	 * @param gruppeId The gruppe ID
	 * @param korrekturStepIds The korrektur step IDs
	 * @param korrektorId The korrektor ID to assign (use "null" to remove assignment)
	 * @param onProgress Optional callback to track progress (completed, total)
	 */
	public async assignKorrekturStepsToKorrektor(
		organizationId: string,
		gruppeId: string,
		korrekturStepIds: string[],
		korrektorId: string,
		onProgress?: (completed: number, total: number) => void,
	): Promise<void> {
		const total = korrekturStepIds.length;

		// If there's a progress callback and more than 5 steps, process them one by one
		// to provide real-time progress updates
		if (onProgress && total > 5) {
			for (let i = 0; i < korrekturStepIds.length; i++) {
				await this.baseApi.put(
					`${this.baseApiSubPath}/${organizationId}/gruppe/${gruppeId}/korrektur-steps/${korrekturStepIds[i]}/korrektor/${korrektorId}`,
					{},
				);
				onProgress(i + 1, total);
			}
		} else {
			// For small batches or no progress callback, use the bulk endpoint
			await this.baseApi.put(
				`${this.baseApiSubPath}/${organizationId}/gruppe/${gruppeId}/korrektur-steps/bulk/korrektor/${korrektorId}`,
				{ korrekturStepIds },
			);
			onProgress?.(total, total);
		}
	}

	/**
	 * Edit the title of a gruppe
	 * @param organizationId The organization ID
	 * @param gruppeId The gruppe ID
	 * @param title The new title
	 */
	public async editGruppeTitle(
		organizationId: string,
		gruppeId: string,
		title: string,
	): Promise<void> {
		await this.baseApi.put(
			`${this.baseApiSubPath}/${organizationId}/gruppe/${gruppeId}/title`,
			{
				title,
			},
		);
	}
}

export default OrgaAdminGruppeService;
